/**
 * @file LoginHistoryDialog.tsx
 * @brief Presentational sheet for a user's login-location history.
 *
 * @details
 * Fetch-agnostic: the caller owns the query and passes `history` + status in.
 * Reused by Users and Employees. Geo is best-effort on the backend.
 *
 * @author DES
 * @date 08-07-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { CircleHelp, Globe, Laptop, LoaderCircle, MapPin, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { GridBand, GridCell } from "@/features/analytics/components/GridBlock";
import { formatDateTime } from "@/shared/utils/formatters";
import { LoginLocationPill } from "@/shared/components/LoginLocationPill";
import { loginClient, type LoginEventRow } from "@/shared/utils/loginLocation";

interface LoginHistoryDialogProps {
  /** Display name of the person whose history this is (for the subtitle). */
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  history: LoginEventRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function LoginHistoryDialog({
  name,
  open,
  onOpenChange,
  history,
  isLoading,
  isError,
}: LoginHistoryDialogProps) {
  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* Opens in the middle like ⌘K, the web form-sheet standard, not as a
          side panel (03-10-2026, DES). */}
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Login history</FormSheetTitle>
            <FormSheetDescription>
              Where <span className="font-medium">{name}</span> has signed in
              from, most recent first.
            </FormSheetDescription>
          </FormSheetHeader>

          <FormSheetBody className="bg-background p-0">
            {isLoading ? (
              <GridCell>
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                </div>
              </GridCell>
            ) : isError ? (
              <GridCell>
                <p className="py-8 text-center text-sm text-destructive">
                  Failed to load login history.
                </p>
              </GridCell>
            ) : !history || history.length === 0 ? (
              <GridCell>
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No recorded logins yet.
                </p>
              </GridCell>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr className="border-b border-border/60">
                      <th className="px-4 py-3 font-medium md:px-5">When</th>
                      <th className="px-4 py-3 font-medium md:px-5">Location</th>
                      <th className="px-4 py-3 font-medium md:px-5">IP</th>
                      <th className="px-4 py-3 font-medium md:px-5">Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((ev, i) => (
                      <tr
                        key={ev.id ?? i}
                        className="border-b border-border/60 last:border-0"
                      >
                        <td className="whitespace-nowrap px-4 py-3 align-top md:px-5">
                          {formatDateTime(ev.created_at)}
                        </td>
                        <td className="px-4 py-3 align-top md:px-5">
                          <LoginLocationPill
                            country={ev.country}
                            region={ev.region}
                            city={ev.city}
                            empty="Unknown"
                          />
                        </td>
                        <td className="px-4 py-3 align-top font-mono text-xs text-muted-foreground md:px-5">
                          {ev.ip_address || "—"}
                        </td>
                        <td
                          className="px-4 py-3 align-top md:px-5"
                          title={ev.user_agent || undefined}
                        >
                          <LoginClientCell userAgent={ev.user_agent} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <GridBand className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Location is derived from the IP address (approximate).
            </GridBand>
          </FormSheetBody>

          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className={formSheetActionClass}
              >
                Close
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

/** Web, Desktop app or Mobile app, from the login's user agent (03-10-2026). */
const CLIENT_ICON = { web: Globe, desktop: Laptop, mobile: Smartphone, unknown: CircleHelp } as const;

function LoginClientCell({ userAgent }: { userAgent?: string | null }) {
  const c = loginClient(userAgent);
  const Icon = CLIENT_ICON[c.kind];
  return (
    <span className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0">
        <span className="block whitespace-nowrap text-sm font-medium text-foreground">{c.label}</span>
        {c.detail ? (
          <span className="block whitespace-nowrap text-xs text-muted-foreground">{c.detail}</span>
        ) : null}
      </span>
    </span>
  );
}
